import { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { sanitizeDiagnosticText } from '@/utils/sanitize';

const MAX_COMPONENT_STACK_LENGTH = 2000;

export { sanitizeDiagnosticText };

export const buildGlobalRenderDiagnostic = (error, info) => {
  let pathname = '';

  if (typeof window !== 'undefined') {
    const { location } = window;
    const { pathname: currentPathname } = location;
    pathname = currentPathname;
  }

  return {
    timestamp: new Date().toISOString(),
    pathname: sanitizeDiagnosticText(pathname),
    errorName: sanitizeDiagnosticText(error && error.name, 120) || 'Error',
    sanitizedMessage: sanitizeDiagnosticText(error && error.message),
    componentStack: sanitizeDiagnosticText(info && info.componentStack, MAX_COMPONENT_STACK_LENGTH),
  };
};

export const reportGlobalRenderError = (error, info) => {
  const diagnostic = buildGlobalRenderDiagnostic(error, info);

  // eslint-disable-next-line no-console
  console.error('[GlobalErrorBoundary]', diagnostic);

  return diagnostic;
};

const reloadPage = () => {
  if (typeof window !== 'undefined' && window.location) {
    window.location.reload();
  }
};

const STORAGE_BLOCKED_CLARITY_EVENT = 'global_error_storage_blocked';
const STORAGE_PROBE_KEY = 'cakto-storage-probe';

const FALLBACK_COPY = {
  default: {
    title: 'Não foi possível carregar esta página',
    description: 'Ocorreu um erro inesperado. Recarregue a página para tentar novamente.',
    action: 'Recarregar página',
  },
  storageBlocked: {
    title: 'O navegador está bloqueando os cookies deste site',
    description:
      'A Cakto precisa guardar o seu acesso no navegador. Permita os cookies deste site nas configurações do navegador, ou abra o link no Chrome ou no Safari.',
    action: 'Já permiti, recarregar',
  },
};

// Com os cookies do site bloqueados, o acesso ao `localStorage` lança `SecurityError`; na WebView
// do Android sem DOM storage, ele é `null`. A sonda só lê: o `localStorageAvailable` grava, e a
// gravação também falha com o armazenamento cheio, quando a sessão ainda é lida e a quebra tem
// outra causa.
const isLocalStorageBlocked = () => {
  try {
    window.localStorage.getItem(STORAGE_PROBE_KEY);
    return false;
  } catch {
    return true;
  }
};

// O Clarity entra por um efeito do `App`, que roda depois do `componentDidCatch` quando a quebra
// acontece na carga. A fila é a do snippet do Clarity: o script executa o que estiver em `q`.
const sendClarityEvent = (name) => {
  if (typeof window.clarity !== 'function') {
    window.clarity = (...args) => {
      (window.clarity.q = window.clarity.q || []).push(args);
    };
  }

  window.clarity('event', name);
};

class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, storageBlocked: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true, storageBlocked: isLocalStorageBlocked() };
  }

  componentDidCatch(error, info) {
    reportGlobalRenderError(error, info);

    const { storageBlocked } = this.state;

    if (storageBlocked) {
      sendClarityEvent(STORAGE_BLOCKED_CLARITY_EVENT);
    }
  }

  handleReload = () => {
    const { onReload } = this.props;

    if (typeof onReload === 'function') {
      onReload();
      return;
    }

    reloadPage();
  };

  render() {
    const { hasError, storageBlocked } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return children;
    }

    const copy = storageBlocked ? FALLBACK_COPY.storageBlocked : FALLBACK_COPY.default;

    return (
      <Box
        role="alert"
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh',
          p: { xs: 3, sm: 6 },
        }}
      >
        <Stack alignItems="center" maxWidth={480} spacing={2} textAlign="center">
          <Typography component="h1" variant="h4">
            {copy.title}
          </Typography>
          <Typography color="text.secondary">{copy.description}</Typography>
          <Button onClick={this.handleReload} variant="contained">
            {copy.action}
          </Button>
        </Stack>
      </Box>
    );
  }
}

GlobalErrorBoundary.propTypes = {
  children: PropTypes.node,
  onReload: PropTypes.func,
};

export default GlobalErrorBoundary;
